import { formatInTimeZone, fromZonedTime } from "date-fns-tz"
import { formatDateTime, PERTH_TIME_ZONE } from "./formatDateTime"
import { InputField } from "./InputField"

type Props = {
  id: string
  label: string
  /** A UTC instant, or none while there is no time yet. */
  value?: string
  /** Reports the UTC instant of the Perth time entered, or none once cleared. */
  onChange: (value: string | undefined) => void
  /** A time to read, not to change: it reads as people say it. */
  readOnly?: boolean
  hint?: string
  required?: boolean
  error?: string
  disabled?: boolean
}

/**
 * A moment in Perth time, whatever the viewer's own zone. Changed in the
 * browser's date and time picker. Read-only, it is drawn as the input field's
 * read-only state, so it sits with the other values a form shows but doesn't
 * take.
 */
export function DateTimeField({ value, onChange, readOnly, ...field }: Props) {
  return (
    <InputField
      {...field}
      type={readOnly ? "text" : "datetime-local"}
      value={shown(value, readOnly)}
      onChange={(wallTime) =>
        onChange(wallTime === "" ? undefined : fromPerthWallTime(wallTime))
      }
      readOnly={readOnly}
    />
  )
}

const shown = (value: string | undefined, readOnly?: boolean): string => {
  if (value === undefined) return ""
  return readOnly ? formatDateTime(value) : toPerthWallTime(value)
}

/** As a date and time input holds it: Perth's wall clock, to the minute. */
const toPerthWallTime = (instant: string): string =>
  formatInTimeZone(instant, PERTH_TIME_ZONE, "yyyy-MM-dd'T'HH:mm")

/** As toISOString writes it: "2026-11-03T18:30:00.000Z". */
const fromPerthWallTime = (wallTime: string): string =>
  fromZonedTime(wallTime, PERTH_TIME_ZONE).toISOString()
